/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Created On: March 23rd, 2018
 * Last Modified: March 23rd, 2018
 * ****************************************************************************
 */

import * as React from 'react';
import { ChessRenderInfo, ChessRank, ChessFile, PieceType, PieceColor, Dimensions } from './chessTypes';
import { ChessSquareComponent, ChessSquareProps, ChessSquareLabel } from './chessSquare';

/** Render prop for rendering a given piece on a square */
export type RenderPieceFunction = (info: ChessRenderInfo) => React.ReactNode;
export type SquarePropsFactory = (square: ChessRenderInfo, props: React.HTMLProps<HTMLDivElement>) => React.HTMLProps<HTMLDivElement>;

/** ChessboardProps - Properties */
export interface ChessboardProps extends React.HTMLProps<HTMLDivElement> {
  fen: string;
  width: number;
  height: number;
  renderPiece: RenderPieceFunction;

  flipBoard?: boolean;
  boardProps?: React.HTMLProps<HTMLDivElement>;
  rankProps?: React.HTMLProps<HTMLDivElement>;
  squareProps?: React.HTMLProps<HTMLDivElement> | SquarePropsFactory;
}

const lowerLetterA = 'a'.charCodeAt(0);
const ranks = Object.freeze<ChessRank>([8, 7, 6, 5, 4, 3, 2, 1]);
const files = Object.freeze<ChessFile>(['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']);
const fenExpand = /[1-8]/g;
const fenSubst = { 1: '-', 2: '--', 3: '---', 4: '----', 5: '-----', 6: '------', 7: '-------', 8: '--------' };

function getStyles(): { [key: string]: React.CSSProperties } {
  return {
    board: { display: 'flex', flexDirection: 'column' },
    rank: { display: 'flex', flexDirection: 'row' },
    square: { display: 'flex', flexDirection: 'column' },
  };
}

function mergeProps(props1?: React.HTMLProps<HTMLDivElement>, props2?: React.HTMLProps<HTMLDivElement>) {
  if (!props1 || !props2) { return props1 || props2; }
  const result = { ...props1, ...props2 };
  if (props1.className && props2.className) {
    result.className = `${props1.className} ${props2.className}`;
  }
  else {
    result.className = props1.className || props2.className;
  }
  if (props1.style && props2.style) {
    result.style = { ...props1.style, ...props2.style };
  }
  const width = props1.width || props2.width;
  const height = props1.height || props2.height;
  if (width || height) {
    result.style = {
      width: `${width || height}px`,
      height: `${height || width}px`,
      ...result.style
    };
  }
  return result;
}



/** Chessboard */
export class Chessboard extends React.PureComponent<ChessboardProps> {
  constructor(props: ChessboardProps, ctx: any) {
    super(props, ctx);
    this.state = {};
  }

  componentDidCatch(error: Error, info: any) {
    console.error('Fatal error in Chessboard component', info, error);
    this.setState({ fatal: error });
  }

  unpackFen(fen: string): string[] {
    const expanded = fen.replace(fenExpand, i => fenSubst[i]);
    return expanded.split('/');
  }

  setSize(bounds?: Dimensions) {
    if (!bounds) {
      return this.setState({ bounds: undefined });
    }
    const { width, height } = bounds;
    this.setState({
      bounds: { width, height }
    });
  }

  render() {
    const { fen, width, height, renderPiece, flipBoard, boardProps, rankProps, squareProps, ...props } = this.props;
    const board = this.unpackFen(fen);

    const styles = getStyles();
    if (flipBoard) {
      styles.board.flexDirection += '-reverse';
      styles.rank.flexDirection += '-reverse';
    }

    const szBoardStyle = { width: `${width}px`, height: `${height}px` };
    const szPiece = { width: (width / 8), height: (height / 8) };
    if (width !== szPiece.width * 8 || height !== szPiece.height * 8) {
      console.warn(`Chessboard: Provide a width/height that evenly divides by 8.`);
    }

    return (
      <div {...mergeProps({ className: `chessboard`, style: { ...styles.board, ...szBoardStyle } }, { ...boardProps })}>
        {ranks.map(rank => (
          <div key={rank} {...mergeProps({ className: `rank rank-${rank}`, style: styles.rank }, rankProps)}>
            {files.map(file => this.renderSquare(szPiece, board, rank, file, styles.square))}
          </div>
        ))}
      </div >
    );
  }

  renderSquare(size: Dimensions, board: string[], rank: ChessRank, file: ChessFile, style: React.CSSProperties) {
    const { renderPiece } = this.props;
    const fileIx = file.charCodeAt(0) - lowerLetterA;
    const squareColor = rank % 2 === (file.charCodeAt(0) - lowerLetterA) % 2 ? 'light' : 'dark';
    const fenPiece = board[8 - rank][fileIx];
    const renderData: ChessRenderInfo = {
      size,
      square: { rank, file, color: squareColor },
      piece: fenPiece === '-' ? undefined : {
        type: fenPiece.toLowerCase() as PieceType,
        color: fenPiece.charCodeAt(0) < lowerLetterA ? 'w' : 'b' as PieceColor
      }
    };

    const { width, height } = size;
    const sizes = {
      overflow: 'hidden',
      width: width + 'px',
      height: height + 'px',
      minWidth: width + 'px',
      minHeight: height + 'px',
      maxWidth: width + 'px',
      maxHeight: height + 'px',
    };

    let squareProps: React.HTMLProps<HTMLDivElement> = { style: { ...style, ...sizes } };
    if (typeof this.props.squareProps === 'function') {
      squareProps = this.props.squareProps(renderData, squareProps) || squareProps;
    }
    else if (typeof this.props.squareProps === 'object') {
      squareProps = { ...squareProps, ...this.props.squareProps };
    }

    const labels: ChessSquareLabel[] = [];
    if (rank === (this.props.flipBoard ? 8 : 1)) {
      labels.push({ type: 'file', className: 'file-label', text: file });
    }
    if (file === (this.props.flipBoard ? 'h' : 'a')) {
      labels.push({ type: 'rank', className: 'rank-label', text: rank.toString() });
    }

    const props: ChessSquareProps = { renderData, renderPiece, labels, squareProps };
    return <ChessSquareComponent key={file} {...props} />;
  }
}

export default Chessboard;



// WEBPACK FOOTER //
// ./src/components/board/chessboard.tsx